<script>
import { mapGetters } from 'vuex';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useConfig } from 'dashboard/composables/useConfig';
import {
  getInboxClassByType,
  getInboxWarningIconClass,
} from 'dashboard/helper/inbox';

import SecondaryChildNavItem from './SecondaryChildNavItem.vue';
import {
  isOnMentionsView,
  isOnUnattendedView,
} from '../../../store/modules/conversations/helpers/actionHelpers';
import Policy from '../../policy.vue';
import LucideIcon from '../../LucideIcon.vue';

export default {
  components: { SecondaryChildNavItem, Policy, LucideIcon },
  props: {
    menuItem: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['addLabel', 'open', 'navigate'],
  setup() {
    const { isAdmin } = useAdmin();
    const { isEnterprise } = useConfig();
    return {
      isAdmin,
      isEnterprise,
    };
  },
  computed: {
    ...mapGetters({
      activeInbox: 'getSelectedInbox',
      accountId: 'getCurrentAccountId',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      globalConfig: 'globalConfig/get',
    }),
    menuItemLabel() {
      // Safe guard để đảm bảo menuItem.label luôn là string hợp lệ
      if (!this.menuItem || !this.menuItem.label || typeof this.menuItem.label !== 'string') {
        return '';
      }
      return this.menuItem.label;
    },
    translationKey() {
      // Tạo key translation an toàn
      if (!this.menuItemLabel) {
        return 'SIDEBAR.UNKNOWN';
      }
      return `SIDEBAR.${this.menuItemLabel}`;
    },
    newLinkTranslationKey() {
      // Tạo key translation an toàn cho new link tag
      if (!this.menuItem || !this.menuItem.newLinkTag || typeof this.menuItem.newLinkTag !== 'string') {
        return 'SIDEBAR.NEW_ITEM';
      }
      return `SIDEBAR.${this.menuItem.newLinkTag}`;
    },
    isCountZero() {
      return this.menuItem.count === 0;
    },
    isActiveView() {
      return this.computedClass.includes('active-view');
    },
    hasSubMenu() {
      return !!this.menuItem.children;
    },
    isMenuItemVisible() {
      if (this.menuItem.globalConfigFlag) {
        // this checks for the `csmlEditorHost` flag in the global config
        // if this is present, we toggle the CSML editor menu item
        // TODO: This is very specific, and can be handled better, fix it
        return !!this.globalConfig[this.menuItem.globalConfigFlag];
      }

      let isFeatureEnabled = true;
      if (this.menuItem.featureFlag) {
        isFeatureEnabled = this.isFeatureEnabledonAccount(
          this.accountId,
          this.menuItem.featureFlag
        );
      }

      if (this.menuItem.featureFlag) {
        return this.isFeatureEnabledonAccount(
          this.accountId,
          this.menuItem.featureFlag
        );
      }

      return isFeatureEnabled;
    },
    isAllConversations() {
      return (
        this.$store.state.route.name === 'inbox_conversation' &&
        this.menuItem.toStateName === 'home'
      );
    },
    isMentions() {
      return (
        isOnMentionsView({ route: this.$route }) &&
        this.menuItem.toStateName === 'conversation_mentions'
      );
    },
    isUnattended() {
      return (
        isOnUnattendedView({ route: this.$route }) &&
        this.menuItem.toStateName === 'conversation_unattended'
      );
    },
    isTeamsSettings() {
      return (
        this.$store.state.route.name === 'settings_teams_edit' &&
        this.menuItem.toStateName === 'settings_teams_list'
      );
    },
    isInboxSettings() {
      return (
        this.$route.name === 'settings_inbox_show' &&
        this.menuItem.toStateName === 'settings_inbox_list'
      );
    },
    isIntegrationsSettings() {
      return (
        this.$store.state.route.name === 'settings_integrations_webhook' &&
        this.menuItem.toStateName === 'settings_integrations'
      );
    },
    isApplicationsSettings() {
      return (
        this.$store.state.route.name === 'settings_applications_integration' &&
        this.menuItem.toStateName === 'settings_applications'
      );
    },
    isContactsDefaultRoute() {
      return (
        this.menuItem.toStateName === 'contacts_dashboard_index' &&
        (this.$store.state.route.name === 'contacts_dashboard_index' ||
          this.$store.state.route.name === 'contacts_edit')
      );
    },
    isCurrentRoute() {
      const currentRoute = this.$store.state.route.name;
      const menuToStateName = this.menuItem.toStateName;

      const routeMap = {
        settings_teams_list: ['settings_teams'],
        settings_inbox_list: ['settings_inbox', 'settings_inboxes'],
        settings_applications: ['settings_integrations', 'settings_applications'],
        agent_list: ['agent_'],
        labels_list: ['labels_'],
        attributes_list: ['attributes_'],
        automation_list: ['automation_'],
        agent_bots: ['agent_bots'],
        macros_wrapper: ['macros_'],
        canned_list: ['canned_'],
        auditlogs_list: ['auditlogs_'],
        custom_roles_list: ['custom_roles_'],
        sla_list: ['sla_'],
        billing_settings_index: ['billing_'],
        chatbot_settings: ['chatbot_'],
        ai_settings: ['ai_'],
        general_settings_index: ['general_settings_'],
        portals_articles_index: ['portals_articles_'],
        portals_categories_index: ['portals_categories_articles_', 'portals_categories_index'],
        portals_locales_index: ['portals_locales_index'],
        portals_settings_index: ['portals_settings_index'],
      };

      if (routeMap[menuToStateName]) {
        return routeMap[menuToStateName].some(prefix =>
          currentRoute.startsWith(prefix)
        );
      }

      return currentRoute.includes(menuToStateName);
    },
    computedClass() {
      // If an inbox filter is active, avoid highlighting only the default Conversations (home) item
      // Allow other sections (Settings, Reports, etc.) to highlight based on current route
      if (this.activeInbox && this.menuItem?.toStateName === 'home') return ' ';
      if (
        this.isAllConversations ||
        this.isMentions ||
        this.isUnattended ||
        this.isContactsDefaultRoute ||
        this.isCurrentRoute
      ) {
        return 'bg-hilab text-white active-view';
      }
      if (this.hasSubMenu) {
        if (
          this.isTeamsSettings ||
          this.isInboxSettings ||
          this.isIntegrationsSettings ||
          this.isApplicationsSettings
        ) {
          return 'text-hilab active-view';
        }
        return 'hover:text-n-slate-12';
      }

      return 'hover:text-n-slate-12';
    },
  },
  methods: {
    computedInboxClass(child) {
      const { type, phoneNumber } = child;
      if (!type) return '';
      const classByType = getInboxClassByType(type, phoneNumber);
      return classByType;
    },
    computedInboxErrorClass(child) {
      const { type, reauthorizationRequired } = child;
      if (!type) return '';
      const warningClass = getInboxWarningIconClass(
        type,
        reauthorizationRequired
      );
      return warningClass;
    },
    newLinkClick(e, navigate) {
      if (this.menuItem.newLinkRouteName) {
        navigate(e);
      } else if (this.menuItem.showModalForNewItem) {
        if (this.menuItem.modalName === 'AddLabel') {
          e.preventDefault();
          this.$emit('addLabel');
        }
      }
    },
    showItem(item) {
      return this.isAdmin && !!item.newLink;
    },
    onClickOpen() {
      this.$emit('open');
    },
    onChildNavigate() {
      // bubble child navigation event upward so parent (Secondary.vue) can react
      this.$emit('navigate');
    },
    showChildCount(count) {
      return Number.isInteger(count);
    },
  },
};
</script>

<template>
  <li v-show="isMenuItemVisible" class="mt-[3px]">
    <div v-if="hasSubMenu" class="flex justify-between w-full">
      <span
        class="px-2 pt-1 my-2 text-sm dark:text-n-gray-4"
      >
        {{ menuItemLabel ? $t(translationKey) : '' }}
      </span>
      <div v-if="menuItem.showNewButton" class="flex items-center">
        <woot-button
          size="tiny"
          variant="clear"
          color-scheme="secondary"
          icon="add"
          class="p-0 ml-2"
          @click="onClickOpen"
        />
      </div>
    </div>
    <router-link
      v-else
      class="flex items-center h-10 p-3 mx-1 my-1 text-sm leading-4 rounded-xl text-n-slate-11 transition-all duration-200 hover:shadow-md hover:scale-[1.02]"
      :class="[computedClass, { 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg': isActive, 'hover:bg-white/60 dark:hover:bg-slate-700/60 hover:backdrop-blur-sm': !isActiveView }]"
      :to="menuItem && menuItem.toState"
      @click="$emit('navigate')"
    >
      <LucideIcon
        :name="menuItem.icon"
        :size="18"
        class="min-w-[1rem] mr-3 rtl:mr-0 rtl:ml-3"
      />
      <span class="truncate">
        {{ $t(`SIDEBAR.${menuItem.label}`) }}
      </span>
      <div class="ml-auto flex items-center">
        <span
          v-if="showChildCount(menuItem.count)"
          class="px-2 py-0 mx-1 rounded-full text-xxs"
          :class="{
            'text-n-slate-10': isCountZero && !isActiveView,
            'text-n-slate-11': !isCountZero && !isActiveView,
            'bg-hilab text-white': isActiveView,
            'bg-n-solid-3': !isActiveView,
          }"
        >
        {{ `${menuItem.count}` }}
       </span>
        <span
          v-if="menuItem.beta"
          data-view-component="true"
          label="Beta"
          class="inline-block px-1 mx-1 leading-4 text-green-500 border border-green-400 rounded-lg text-xxs"
        >
          {{ $t('SIDEBAR.BETA') }}
        </span>
      </div>
    </router-link>

    <ul v-if="hasSubMenu" class="list-none reset-base">
      <SecondaryChildNavItem
        v-for="child in menuItem.children"
        :key="child.id"
        :to="child.toState"
        :label="child.label"
        :label-color="child.color"
        :should-truncate="child.truncateLabel"
        :icon="(child.brandIcon ? '' : (child.icon || computedInboxClass(child)))"
        :warning-icon="computedInboxErrorClass(child)"
        :show-child-count="showChildCount(child.count)"
        :child-item-count="child.count"
        :brand-icon="child.brandIcon"
        @navigate="onChildNavigate"
      />
      <Policy :permissions="['administrator']">
        <router-link
          v-if="menuItem.newLink"
          v-slot="{ href, navigate }"
          :to="menuItem.toState"
          custom
        >
          <li class="pl-1">
            <a :href="href">
              <woot-button
                size="tiny"
                variant="clear"
                color-scheme="secondary"
                icon="add"
                :data-testid="menuItem.dataTestid"
                @click="e => newLinkClick(e, navigate)"
              >
                {{ menuItem.newLinkTag ? $t(newLinkTranslationKey) : '' }}
              </woot-button>
            </a>
          </li>
        </router-link>
      </Policy>
    </ul>
  </li>
</template>
